import { type ReactNode, useMemo } from 'react';
import { isError } from 'lodash-es';
import { type FallbackProps } from 'react-error-boundary';
import { css } from '@styled-system/css';
import { getErrorStrings, resolveErrorLocale } from '@tecnobit-srl/i18n';

export function ErrorFallback({ error }: FallbackProps): ReactNode {
  // NOTE: this fallback is rendered by the top-level ErrorBoundary, which sits
  // ABOVE the RouterProvider. It must not depend on router (or any other app
  // provider) context, otherwise the fallback itself throws (e.g. "Missing
  // Router provider in the tree") and masks the original error. Build the
  // reload URL from window.location instead of usePage(). Localized strings
  // come from the static error dictionary via resolveErrorLocale() — no router
  // or i18n hooks, or the fallback itself throws and masks the original error.
  const url = useMemo(() => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('t', new Date().getTime().toString());
      return u.toString();
    } catch {
      return `/?t=${new Date().getTime().toString()}`;
    }
  }, []);

  const strings = useMemo(() => getErrorStrings(resolveErrorLocale()), []);

  return (
    <div
      className={css({
        minH: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bg: 'gray.200',
        color: 'text.500',
        px: '6',
      })}
    >
      <div
        className={css({
          maxW: '420px',
          textAlign: 'center',
          p: '8',
          borderRadius: 'lg',
          bg: 'white',
          boxShadow: 'lg',
        })}
      >
        <h1
          className={css({
            fontSize: 'xl',
            fontWeight: 'bold',
            mb: '2',
          })}
        >
          {strings.title}
        </h1>

        <p
          className={css({
            fontSize: 'sm',
            opacity: 0.8,
            mb: '6',
          })}
        >
          {strings.description}
        </p>

        <div
          className={css({
            display: 'flex',
            gap: '3',
            justifyContent: 'center',
          })}
        >
          <a
            href={url}
            onClick={(e) => {
              e.preventDefault();
              // @ts-expect-error Alcuni browser supportano "true" per forzare svuotamento della cache
              window.location.reload(true);
            }}
            className={css({
              cursor: 'pointer',
              px: '4',
              py: '2',
              borderRadius: 'md',
              bg: 'accent.500',
              color: 'overAccent.500',
              textDecoration: 'none',
              _hover: { opacity: 0.9 },
            })}
          >
            {strings.retry}
          </a>

          <a
            href="/"
            title={strings.home}
            className={css({
              px: '4',
              py: '2',
              borderRadius: 'md',
              borderWidth: '1px',
              color: 'accent.500',
              textDecoration: 'none',
              _hover: { color: 'accent.700' },
            })}
          >
            {strings.home}
          </a>
        </div>

        {import.meta.env.DEV ? (
          <pre
            className={css({
              mt: '6',
              fontSize: 'xs',
              textAlign: 'left',
              opacity: 0.6,
              overflowX: 'auto',
            })}
          >
            {isError(error) ? error.message : 'unknown'}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
